import { useCallback, useEffect, useRef, useState } from "react";
import { ESTADOS_FINALIZADOS } from "../constants";
import { conteosDeLinea } from "../helpers/conteos";
import { leerBorradorLocal, escribirBorradorLocal } from "../utils/borradorLocal";

const esCantidadValida = (valor) => Number.isInteger(valor) && valor >= 0;

/**
 * Borrador recuperado del dispositivo como { lineaId: { almacen: cantidad } }, solo lo que
 * aún tiene sentido: artículos que siguen en la lista y cantidades distintas de las guardadas
 */
const filtrarRecuperable = (porReferencia, inventario) =>
    Object.fromEntries(
        Object.entries(porReferencia ?? {}).flatMap(([referencia, porAlmacen]) => {
            const linea = inventario.lineas?.find((item) => item.referencia === referencia);
            if (!linea || !porAlmacen || typeof porAlmacen !== "object") return [];
            const conteos = conteosDeLinea(linea);
            const validos = Object.entries(porAlmacen).filter(
                ([almacen, cantidad]) =>
                    esCantidadValida(cantidad) && conteos.find((conteo) => conteo.almacen === almacen)?.cantidadContada !== cantidad
            );
            return validos.length > 0 ? [[linea.id, Object.fromEntries(validos)]] : [];
        })
    );

/**
 * Hook que copia el borrador de conteo en el dispositivo y lo recupera al volver
 * a abrir el inventario (salir de la pantalla o recargar no lo pierde). Con
 * `activo` false no lee ni escribe nada
 * @returns {{ recuperados: number, descartarRecuperados: Function, ocultarAviso: Function }}
 */
export const useBorradorLocal = ({ activo, inventario, borrador, setBorrador, almacenGeneral, setAlmacenGeneral }) => {
    const [recuperados, setRecuperados] = useState(0);
    const recuperado = useRef({ id: null, borrador: {} });
    const id = inventario?.id;
    const preparadorId = inventario?.preparadorId;
    const finalizado = Boolean(inventario) && ESTADOS_FINALIZADOS.includes(inventario.estado);

    // Solo una vez por inventario, al tenerlo cargado
    useEffect(() => {
        if (!activo || id == null || recuperado.current.id === id) return;
        recuperado.current = { id, borrador: {} };
        if (finalizado) {
            escribirBorradorLocal(id, preparadorId, null);
            return;
        }
        const guardado = leerBorradorLocal(id, preparadorId);
        if (!guardado) return;
        const recuperable = filtrarRecuperable(guardado.porReferencia, inventario);
        const total = Object.values(recuperable).reduce((suma, porAlmacen) => suma + Object.keys(porAlmacen).length, 0);
        recuperado.current.borrador = recuperable;
        if (total > 0) {
            // Nave a nave: lo que ya haya en el borrador manda, pero no tapa otras naves recuperadas
            setBorrador((prev) => {
                const fusionado = { ...prev };
                Object.entries(recuperable).forEach(([lineaId, porAlmacen]) => {
                    fusionado[lineaId] = { ...porAlmacen, ...(prev[lineaId] ?? {}) };
                });
                return fusionado;
            });
            setRecuperados(total);
        }
        if (guardado.almacenGeneral) setAlmacenGeneral((prev) => prev ?? guardado.almacenGeneral);
    }, [activo, id, preparadorId, finalizado, inventario, setBorrador, setAlmacenGeneral]);

    useEffect(() => {
        if (!activo || id == null || recuperado.current.id !== id) return;
        if (finalizado) {
            escribirBorradorLocal(id, preparadorId, null);
            return;
        }
        const porReferencia = Object.fromEntries(
            Object.entries(borrador).flatMap(([lineaId, porAlmacen]) => {
                const linea = inventario.lineas?.find((item) => item.id === Number(lineaId));
                return linea ? [[linea.referencia, porAlmacen]] : [];
            })
        );
        // Sin cantidades pendientes no se guarda nada, ni siquiera la nave: no quedan copias huérfanas
        const hayBorrador = Object.keys(porReferencia).length > 0;
        escribirBorradorLocal(id, preparadorId, hayBorrador ? { porReferencia, almacenGeneral } : null);
    }, [activo, id, preparadorId, finalizado, inventario, borrador, almacenGeneral]);

    /** Quita del borrador lo recuperado que no se ha tocado desde entonces. */
    const descartarRecuperados = useCallback(() => {
        const aDescartar = recuperado.current.borrador;
        setBorrador((prev) =>
            Object.fromEntries(
                Object.entries(prev).flatMap(([lineaId, porAlmacen]) => {
                    const quedan = Object.entries(porAlmacen).filter(([almacen, cantidad]) => aDescartar[lineaId]?.[almacen] !== cantidad);
                    return quedan.length > 0 ? [[lineaId, Object.fromEntries(quedan)]] : [];
                })
            )
        );
        setRecuperados(0);
    }, [setBorrador]);

    const ocultarAviso = useCallback(() => setRecuperados(0), []);

    return { recuperados, descartarRecuperados, ocultarAviso };
};
